import { FC, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import useOverflowDetection from 'src/common/hooks/useOverflowDetection';
import { UserDetails } from 'src/common/types/api/v4/boats/search';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import DescriptionModal from 'src/listing/components/DescriptionModal';
import getClassNames from 'src/common/utils/getClassNames';

import styles from './Description.module.scss';

export type DescriptionProps = {
  boatOwner: UserDetails;
  description: string;
};

const Description: FC<DescriptionProps> = ({
  boatOwner,
  description,
}) => {
  const [t] = useTranslation('listing-page');
  const { isOpen, open, close } = useOpenCloseToggle();

  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const isTruncated = useOverflowDetection({ ref: descriptionRef });

  if (!description?.length) return null;

  return (
    <>
      <div className={styles.root}>
        <Typography type="h2" size="body-xl" weight="semibold">
          {t('owner-description--title')}
        </Typography>
        <p
          ref={descriptionRef}
          className={styles.truncatedDescription}
        >
          {description}
        </p>
        <div className={getClassNames(styles, 'buttonContainer', classNames({
          hidden: !isTruncated,
        }))}
        >
          <Button
            variant="tertiary-deemphasized"
            size="x-small"
            onClick={open}
            fitWidth
            withoutPadding
          >
            {t('owner-description--read-more')}
          </Button>
        </div>
      </div>
      <DescriptionModal
        description={description}
        boatOwner={boatOwner}
        isOpen={isOpen}
        close={close}
      />
    </>
  );
};

export default Description;
